"use client";

import type { CSSProperties, ReactNode } from "react";

import styles from "./uk-dot-map.module.css";

/*
 * The United Kingdom as a dot-matrix map: coarse coastlines (longitude,
 * latitude) sampled on a hexagonal grid. Accurate enough to read as the UK
 * at a glance, not a survey; it makes no claim about where anything runs.
 */

type LonLat = readonly [number, number];

const GREAT_BRITAIN: readonly LonLat[] = [
  [-5.72, 50.06], [-5.2, 49.96], [-5.05, 50.15], [-4.15, 50.33], [-3.64, 50.22], [-3.52, 50.46], [-3.4, 50.61], [-2.93, 50.72],
  [-2.46, 50.51], [-1.95, 50.6], [-1.4, 50.78], [-0.79, 50.72], [-0.14, 50.82], [0.24, 50.73], [0.97, 50.91], [1.33, 51.12],
  [1.45, 51.38], [1.12, 51.37], [0.75, 51.44], [0.45, 51.46], [0.72, 51.54], [0.95, 51.6], [1.16, 51.79], [1.33, 51.95],
  [1.58, 52.09], [1.76, 52.48], [1.74, 52.61], [1.7, 52.72], [1.3, 52.93], [0.49, 52.95], [0.4, 52.8], [0.1, 52.97],
  [0.34, 53.14], [0.27, 53.34], [0.12, 53.57], [-0.3, 53.73], [-0.16, 53.91], [-0.08, 54.12], [-0.4, 54.28], [-0.61, 54.49],
  [-1.07, 54.62], [-1.19, 54.69], [-1.37, 54.91], [-1.42, 55.02], [-1.57, 55.34], [-2.0, 55.77], [-2.13, 55.91], [-2.52, 56.0],
  [-2.72, 56.06], [-3.15, 55.98], [-3.15, 56.11], [-2.58, 56.28], [-2.79, 56.34], [-2.97, 56.46], [-2.58, 56.56], [-2.46, 56.71],
  [-2.2, 56.96], [-2.07, 57.15], [-1.77, 57.5], [-2.0, 57.69], [-2.52, 57.67], [-3.28, 57.72], [-4.22, 57.49], [-3.78, 57.86],
  [-3.65, 58.12], [-3.09, 58.44], [-3.02, 58.64], [-3.37, 58.67], [-3.52, 58.6], [-4.42, 58.55], [-5.0, 58.63], [-5.24, 58.15],
  [-5.4, 57.9], [-5.75, 57.72], [-5.83, 57.43], [-5.71, 57.28], [-5.83, 56.99], [-6.23, 56.73], [-5.47, 56.41], [-5.57, 56.08],
  [-5.8, 55.3], [-5.6, 55.43], [-5.35, 55.87], [-4.76, 55.95], [-4.87, 55.79], [-4.63, 55.46], [-4.86, 55.24], [-5.03, 54.91],
  [-4.86, 54.63], [-4.4, 54.78], [-4.05, 54.8], [-3.26, 54.97], [-3.39, 54.87], [-3.57, 54.64], [-3.64, 54.51], [-3.23, 54.11],
  [-2.87, 54.07], [-3.01, 53.92], [-3.06, 53.81], [-3.01, 53.65], [-3.07, 53.44], [-3.18, 53.39], [-3.49, 53.32], [-3.83, 53.33],
  [-4.13, 53.23], [-4.35, 53.42], [-4.63, 53.31], [-4.5, 53.2], [-4.33, 53.12], [-4.76, 52.8], [-4.41, 52.88], [-4.12, 52.86],
  [-4.06, 52.72], [-4.09, 52.41], [-4.35, 52.21], [-4.68, 52.1], [-5.07, 52.03], [-5.31, 51.9], [-5.18, 51.68], [-4.7, 51.67],
  [-4.33, 51.56], [-3.94, 51.61], [-3.7, 51.48], [-3.17, 51.45], [-2.95, 51.55], [-2.65, 51.62], [-2.71, 51.5], [-2.98, 51.34],
  [-3.47, 51.21], [-3.83, 51.23], [-4.12, 51.21], [-4.53, 51.02], [-4.55, 50.83], [-5.03, 50.55], [-5.08, 50.42], [-5.48, 50.21],
];

const NORTHERN_IRELAND: readonly LonLat[] = [
  [-5.67, 54.66], [-5.53, 54.38], [-5.6, 54.37], [-5.89, 54.21], [-6.1, 54.05], [-6.4, 54.1], [-7.0, 54.2], [-7.4, 54.15],
  [-7.7, 54.2], [-8.15, 54.47], [-7.8, 54.6], [-7.45, 54.82], [-7.3, 55.04], [-6.95, 55.17], [-6.66, 55.21], [-6.51, 55.24],
  [-6.24, 55.2], [-6.06, 55.19], [-5.8, 54.85], [-5.8, 54.71],
];

const ISLANDS: readonly (readonly LonLat[])[] = [
  [[-1.58, 50.66], [-1.3, 50.77], [-1.07, 50.68], [-1.3, 50.58]],
  [[-4.8, 54.05], [-4.4, 54.4], [-4.33, 54.25], [-4.65, 54.07]],
  [[-6.15, 57.7], [-5.7, 57.55], [-5.95, 57.15], [-6.4, 57.25], [-6.6, 57.45]],
  [[-6.35, 56.6], [-5.7, 56.5], [-5.9, 56.3], [-6.4, 56.3]],
  [[-6.25, 58.5], [-6.18, 58.2], [-6.6, 57.9], [-7.1, 57.6], [-7.25, 57.1], [-7.45, 57.0], [-7.55, 57.3], [-7.1, 57.8], [-7.05, 58.2], [-6.6, 58.4]],
  [[-3.35, 59.1], [-2.4, 59.05], [-2.8, 58.8], [-3.3, 58.85]],
];

const LON_MIN = -8.3;
const LON_MAX = 1.9;
const LAT_MIN = 49.85;
const LAT_MAX = 59.2;
/** Longitude shrinks with latitude; at the UK's middle a degree east is about 0.58 of a degree north. */
const X_SCALE = Math.cos((54.5 * Math.PI) / 180);

export const UK_MAP_WIDTH = (LON_MAX - LON_MIN) * X_SCALE * 100;
export const UK_MAP_HEIGHT = (LAT_MAX - LAT_MIN) * 100;

export function projectLonLat([lon, lat]: LonLat): { x: number; y: number } {
  return { x: (lon - LON_MIN) * X_SCALE * 100, y: (LAT_MAX - lat) * 100 };
}

function inside(x: number, y: number, polygon: readonly { x: number; y: number }[]): boolean {
  let within = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index, index += 1) {
    const a = polygon[index]!;
    const b = polygon[previous]!;
    if (a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) within = !within;
  }
  return within;
}

const STEP = 14;
const LANDS = [GREAT_BRITAIN, NORTHERN_IRELAND, ...ISLANDS].map((polygon) => polygon.map(projectLonLat));

/** The land, as dots on a hexagonal grid, each with its distance from the sweep's start (the south coast). */
const DOTS: readonly { x: number; y: number; order: number }[] = (() => {
  const dots: { x: number; y: number; order: number }[] = [];
  let row = 0;
  for (let y = STEP / 2; y < UK_MAP_HEIGHT; y += STEP * 0.866, row += 1) {
    for (let x = STEP / 2 + (row % 2) * (STEP / 2); x < UK_MAP_WIDTH; x += STEP) {
      if (LANDS.some((land) => inside(x, y, land))) dots.push({ x, y, order: (UK_MAP_HEIGHT - y) / UK_MAP_HEIGHT });
    }
  }
  return dots;
})();

/**
 * The map. Its dots sweep in from the south coast to the north when it
 * mounts; `color` is the land's dot colour.
 */
export function UkDotMap({
  className = "",
  color = "currentColor",
  sweepMs = 1400,
  children,
}: {
  className?: string;
  color?: string;
  sweepMs?: number;
  /** Anything drawn over the land, in the map's own coordinates. */
  children?: ReactNode;
}) {
  return (
    <svg viewBox={`0 0 ${UK_MAP_WIDTH} ${UK_MAP_HEIGHT}`} className={className} aria-hidden>
      <g fill={color}>
        {DOTS.map((dot) => (
          <circle
            key={`${dot.x.toFixed(1)}:${dot.y.toFixed(1)}`}
            cx={dot.x}
            cy={dot.y}
            r={STEP * 0.27}
            className={styles.dot}
            style={{ animationDelay: `${Math.round(dot.order * sweepMs)}ms` } as CSSProperties}
          />
        ))}
      </g>
      {children}
    </svg>
  );
}
